import { Button, Card, Form, Input, message, Select, Space, Spin } from "antd";
import type { RcFile } from "antd/es/upload";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberWithError,
} from "libphonenumber-js";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ContentSkeleton from "../../../components/common/ContentSkeleton";
import { PERMISSIONS } from "../../../constants/permissions";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { sellerApi } from "../../../services/api";
import { RootState } from "../../../store";
import {
  SellerFormData,
  SellerDesignation,
  SellerInfo,
} from "../../../types/seller.types";
import { getPhotoUrl } from "../../../utils/media";
import {
  validatePassword,
  validatePhoneNumber,
} from "../../../utils/validation";

const { Option } = Select;

const SellerFormPage: React.FC = () => {
  const [form] = Form.useForm<SellerFormData>();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("BD");
  const navigate = useNavigate();
  const location = useLocation();
  const { uploadFile, uploading: uploadingFile } = useFileUpload({});
  const { user, permissions } = useSelector((state: RootState) => state.auth);

  const countries = getCountries();
  const isAdmin = user?.designation?.toUpperCase().includes("ADMIN");
  const isDistributor = user?.designation
    ?.toUpperCase()
    .includes("DISTRIBUTOR");
  const isDealer = user?.designation?.toUpperCase().includes("DEALER");
  const hasPermission = (permissionKey: string) => {
    return permissions.some((p) => p.name === permissionKey);
  };

  const canManagePassword = hasPermission(PERMISSIONS.MANAGE_PASSWORD.name);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const sellerId = location.state?.id;

        if (sellerId) {
          const response = await sellerApi.getById(sellerId);
          if (response.status === "SUCCESS") {
            const { photo, ...safeFormData } = response.data;
            form.setFieldsValue(safeFormData);
            if (photo && typeof photo === "string") {
              setPreviewUrl(getPhotoUrl(photo));
            }
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch seller data";
        message.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [form, location.state]);

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG files!");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return false;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    setSelectedPhoto(file);

    return false;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatter = new AsYouType(selectedCountry);
    const formattedNumber = formatter.input(e.target.value);
    form.setFieldsValue({ phone: formattedNumber });
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value as CountryCode);
    const phoneValue = form.getFieldValue("phone");
    if (phoneValue) {
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phoneValue,
          selectedCountry
        );
        if (phoneNumber.isValid()) {
          form.setFieldsValue({ phone: phoneNumber.format("NATIONAL") });
        }
      } catch (error) {
        setApiErrors({
          phone: "Invalid phone number",
        });
      }
    }
  };

  const onFinish = async (values: SellerFormData) => {
    try {
      setLoading(true);

      const phoneNumber = parsePhoneNumberWithError(
        values.phone,
        selectedCountry
      );
      const formattedPhone = phoneNumber.format("E.164");

      const formData: any = {};
      Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData[key] = value.toString();
        }
      });
      formData.phone = formattedPhone;

      if (location.state?.id) {
        formData.id = location.state.id.toString();
      }

      const response = location.state?.id
        ? await sellerApi.update(formData)
        : await sellerApi.create(formData);

      if (response.status === "SUCCESS") {
        message.success(
          location.state?.id
            ? "Seller updated successfully"
            : "Seller added successfully"
        );
        form.resetFields();
        setPreviewUrl(null);
        setSelectedPhoto(null);
        navigate(PERMISSIONS.LIST_SELLERS.route);
      } else {
        const status = response.status;
        const responseMessage = response.message;
        if (status === "VALIDATION_ERROR") {
          setApiErrors(response.data as unknown as Record<string, string>);
        } else {
          message.error(responseMessage || "Failed to save seller");
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save seller";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setPreviewUrl(null);
    setSelectedPhoto(null);
  };

  const getDesignationOptions = () => {
    let options = [];

    if (isAdmin) {
      options.push({
        value: SellerDesignation.DISTRIBUTOR,
        label: "Distributor",
      });
    }

    if (isAdmin || isDistributor) {
      options.push({
        value: SellerDesignation.DEALER,
        label: "Dealer",
      });
    }

    if (isAdmin || isDealer) {
      options.push({
        value: SellerDesignation.RETAILER,
        label: "Retailer",
      });
    }

    return options;
  };

  const clearApiError = (fieldName: string) => {
    setApiErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  if (loading) {
    return <ContentSkeleton cardCount={1} rows={8} />;
  }

  return (
    <Card
      title={location.state?.id ? "Edit Seller" : "Add Seller"}
      style={{ maxWidth: 800, margin: "0 auto" }}
      extra={
        <Button onClick={() => navigate(PERMISSIONS.LIST_SELLERS.route)}>
          Back to List
        </Button>
      }
    >
      <Spin
        spinning={loading || uploadingFile}
        tip={location.state?.id ? "Updating seller..." : "Adding seller..."}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            country: selectedCountry,
            designation:
              getDesignationOptions().length > 0
                ? getDesignationOptions()[0].value
                : "",
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: "Please enter name" },
              {
                min: 3,
                max: 100,
                message: "Name must be between 3 and 100 characters",
              },
            ]}
          >
            <Input
              placeholder="Enter name"
              onChange={() => clearApiError("name")}
            />
          </Form.Item>
          {apiErrors.name && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.name}
            </div>
          )}

          <Form.Item
            name="designation"
            label="Designation"
            rules={[{ required: true, message: "Please select designation" }]}
          >
            <Select
              placeholder="Select designation"
              onChange={() => clearApiError("designation")}
            >
              {getDesignationOptions().map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {apiErrors.designation && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.designation}
            </div>
          )}

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              placeholder="Enter email"
              onChange={() => clearApiError("email")}
            />
          </Form.Item>
          {apiErrors.email && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.email}
            </div>
          )}

          <Form.Item label="Phone Number" required style={{ marginBottom: 0 }}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item
                name="countryCode"
                initialValue={selectedCountry || "BD"}
                style={{ width: "30%" }}
              >
                <Select onChange={handleCountryChange}>
                  {countries.map((country: CountryCode) => (
                    <Option key={country} value={country}>
                      {country} (+{getCountryCallingCode(country)})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="phone"
                style={{ width: "70%" }}
                rules={[
                  {
                    validator: (_, value) =>
                      validatePhoneNumber(_, value, selectedCountry),
                  },
                ]}
              >
                <Input
                  onChange={(e) => {
                    handlePhoneChange(e);
                    clearApiError("phone");
                  }}
                  placeholder="Enter phone number"
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          {apiErrors.phone && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.phone}
            </div>
          )}

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: "Please enter address" }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter address"
              onChange={() => clearApiError("address")}
            />
          </Form.Item>
          {apiErrors.address && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.address}
            </div>
          )}

          <Form.Item
            name="organization"
            label="Shop/Organization Name"
            rules={[
              { required: true, message: "Shop/Organization name is required" },
              {
                min: 3,
                max: 100,
                message:
                  "Shop/Organization name must be between 3 and 100 characters",
              },
            ]}
          >
            <Input
              placeholder="Enter shop/organization name"
              onChange={() => clearApiError("organization")}
            />
          </Form.Item>
          {apiErrors.organization && (
            <div
              style={{
                color: "#ff4d4f",
                fontSize: "12px",
                marginTop: "-12px",
                marginBottom: "12px",
              }}
            >
              {apiErrors.organization}
            </div>
          )}

          {!location.state?.id && (
            <>
              <Form.Item
                name="username"
                label="Username"
                rules={[
                  { required: true, message: "Please enter username" },
                  {
                    pattern: /^[a-zA-Z0-9]{3,100}$/,
                    message:
                      "Username must be 3 to 100 alphanumeric characters",
                  },
                ]}
              >
                <Input
                  placeholder="Enter username"
                  onChange={() => clearApiError("username")}
                />
              </Form.Item>
              {apiErrors.username && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "-12px",
                    marginBottom: "12px",
                  }}
                >
                  {apiErrors.username}
                </div>
              )}
            </>
          )}

          {(canManagePassword || !location.state?.id) && (
            <>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  {
                    validator: (_, value) =>
                      validatePassword(_, value, !location.state?.id),
                  },
                ]}
                hasFeedback
              >
                <Input.Password
                  placeholder="Enter password"
                  onChange={() => clearApiError("password")}
                />
              </Form.Item>
              {apiErrors.password && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "-12px",
                    marginBottom: "12px",
                  }}
                >
                  {apiErrors.password}
                </div>
              )}
            </>
          )}

          {(canManagePassword || !location.state?.id) && (
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["password"]}
              hasFeedback
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject("The passwords do not match");
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm password" />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading || uploadingFile}
              >
                {location.state?.id ? "Update" : "Save"}
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default SellerFormPage;
